import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Alert, Button } from '@mui/material';
import { apiService } from '../services/api';
import { buildImageUrl } from '../config/apiConfig';

const BannerCarousel = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const response = await apiService.getBanners();
      if (response.success && response.data) {
        const limitedBanners = response.data.slice(0, 5);
        setBanners(limitedBanners);
      } else {
        setError('Failed to fetch banners');
      }
    } catch (err) {
      setError('Error loading banners: ' + err.message);
      console.error('Banner fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (banners.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % banners.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [banners.length]);

  const handleCtaClick = (banner) => {
    if (banner.cta_url) {
      window.open(banner.cta_url, '_blank');
    } else {
      console.log('No URL set for CTA:', banner.cta);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: { xs: '250px', sm: '350px', md: '450px' },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#f5f5f5',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || banners.length === 0) {
    return (
      <Alert severity="warning" sx={{ mb: 3 }}>
        {error || '📭 No banners available'}
      </Alert>
    );
  }

  const currentBanner = banners[currentIndex];
  const bannerImageUrl = buildImageUrl(currentBanner.image_url, 'banners') || 'https://via.placeholder.com/1200x400?text=Banner+Image';

  return (
    <Box sx={{ width: '100%', mb: 4, position: 'relative' }}>
      {/* Banner Image Container */}
      <Box
        sx={{
          width: '100%',
          height: { xs: '250px', sm: '350px', md: '450px' },
          overflow: 'hidden',
          borderRadius: { xs: 1, md: 2 },
          boxShadow: 3,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f0f0f0',
        }}
      >
        {/* Banner Image */}
        <Box
          component="img"
          src={bannerImageUrl}
          alt={currentBanner.title}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'opacity 0.5s ease-in-out',
          }}
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/1200x400?text=Banner+Image';
          }}
        />

        {/* Overlay with Text */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
            color: 'white',
            p: { xs: 2, md: 4 },
            textAlign: 'left',
          }}
        >
          <Box sx={{ fontSize: { xs: '1.2rem', md: '2rem' }, fontWeight: 700, mb: 1 }}>
            {currentBanner.title}
          </Box>
          <Box sx={{ fontSize: { xs: '0.875rem', md: '1.125rem' }, mb: 2, opacity: 0.9 }}>
            {currentBanner.subtitle}
          </Box>
          
          {/* CTA Button - Now Clickable */}
          {currentBanner.cta && (
            <Button
              variant="contained"
              sx={{
                bgcolor: '#667eea',
                color: 'white',
                px: { xs: 2, md: 3 },
                py: { xs: 1, md: 1.5 },
                fontWeight: 600,
                fontSize: { xs: '0.875rem', md: '1rem' },
                textTransform: 'none',
                transition: 'all 0.3s',
                '&:hover': {
                  bgcolor: '#5568d3',
                  transform: 'scale(1.05)',
                },
              }}
              onClick={() => handleCtaClick(currentBanner)}
            >
              {currentBanner.cta}
            </Button>
          )}
        </Box>
      </Box>

      {/* Navigation Dots */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1,
          mt: 2,
          pb: 2,
        }}
      >
        {banners.map((banner, index) => (
          <Box
            key={banner.id}
            onClick={() => setCurrentIndex(index)}
            sx={{
              width: currentIndex === index ? '32px' : '8px',
              height: '8px',
              backgroundColor: currentIndex === index ? '#667eea' : '#ddd',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                backgroundColor: '#667eea',
              },
            }}
          />
        ))}
      </Box>

      {/* Banner Counter */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          color: 'white',
          px: 2,
          py: 1,
          borderRadius: 1,
          fontSize: '0.875rem',
          fontWeight: 600,
        }}
      >
        {currentIndex + 1} / {banners.length}
      </Box>
    </Box>
  );
};

export default BannerCarousel;